import { executeOps } from "../bridge/office-ops.ts";
import { parseDeck } from "../pptx.ts";

export type SlideMapping = {
  slideToReplace: number | null;
  slideToInsert: number;
};

export type AppliedSlideMapping = SlideMapping & {
  deckSlideIndex: number;
};

const base64ToBytes = (base64: string): Uint8Array => {
  const binary = globalThis.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const parseInsertResult = (
  raw: unknown,
): { firstIndex: number; count: number } => {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("insertSlidesFromBase64 returned non-object");
  }
  let firstIndex = -1;
  let count = -1;
  for (const [k, v] of Object.entries(raw)) {
    if (k === "firstIndex" && typeof v === "number") firstIndex = v;
    if (k === "count" && typeof v === "number") count = v;
  }
  if (firstIndex < 0 || count < 0) {
    throw new Error("insertSlidesFromBase64 missing firstIndex/count");
  }
  return { firstIndex, count };
};

export const applySlideMappings = async (
  base64: string,
  mappings: readonly SlideMapping[],
): Promise<{ applied: AppliedSlideMapping[] }> => {
  const parsed = await parseDeck(base64ToBytes(base64));
  const applied: AppliedSlideMapping[] = [];

  for (const mapping of mappings) {
    const source = parsed[mapping.slideToInsert];
    if (!source) {
      throw new Error(
        `Source pptx has no slide at index ${mapping.slideToInsert.toString()} (${parsed.length.toString()} slides)`,
      );
    }

    if (mapping.slideToReplace === null) {
      const insertResults = await executeOps([
        {
          op: "insertSlidesFromBase64",
          base64,
          sourceSlideIds: [source.slideId],
        },
      ]);
      const insertRes = parseInsertResult(insertResults[0]);
      if (insertRes.count < 1) {
        throw new Error(
          `Failed to append source slide ${mapping.slideToInsert.toString()}`,
        );
      }
      applied.push({
        ...mapping,
        deckSlideIndex: insertRes.firstIndex,
      });
      continue;
    }

    await executeOps([
      {
        op: "replaceSlideFromBase64",
        base64,
        sourceSlideId: source.slideId,
        currentSlideIndex: mapping.slideToReplace,
      },
    ]);
    applied.push({
      ...mapping,
      deckSlideIndex: mapping.slideToReplace,
    });
  }

  return { applied };
};

export const insertAllSlidesAsScratch = async (
  base64: string,
  scratchTag: string,
): Promise<{ firstIndex: number; count: number; scratchTag: string }> => {
  const insertResults = await executeOps([
    { op: "insertSlidesFromBase64", base64 },
  ]);
  const insertRes = parseInsertResult(insertResults[0]);
  if (insertRes.count > 0) {
    await executeOps([
      {
        op: "tagScratchSlides",
        firstIndex: insertRes.firstIndex,
        count: insertRes.count,
        scratchTag,
      },
    ]);
  }
  return { ...insertRes, scratchTag };
};
